import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { NgControl, Validators } from '@angular/forms';
import { createCustomValidation } from './custom-validation.validation';

export interface CustomValidationConfiguration {
  mask?: string;
  maskRegexp?: string;
  maxLength?: number | null;
  transform?: 'lowercase' | 'none' | 'uppercase' | null;
  validationMessage?: string;
  validationRegexp?: string;
}

@Directive({
  selector: '[palantirCustomValidation]',
  standalone: true,
})
export class PalantirCustomValidationDirective implements OnChanges {
  @Input('palantirCustomValidation') public configuration?: CustomValidationConfiguration;

  private regExpValidationFn = createCustomValidation(this.configuration ?? {});

  private maxLengthValidatorFn = this.configuration?.maxLength
    ? Validators.maxLength(this.configuration?.maxLength)
    : null;

  public constructor(
    private readonly element: ElementRef<HTMLInputElement>,
    private readonly control: NgControl,
    private readonly renderer: Renderer2,
  ) {}

  private isRegexpValidationChanged(changes: SimpleChanges): boolean {
    if (changes['configuration']?.currentValue) {
      const newRegexp = changes['configuration'].currentValue.validationRegexp;
      const oldRegexp = changes['configuration'].previousValue?.validationRegexp;
      return newRegexp !== oldRegexp;
    }

    return false;
  }

  private isMaxLengthChanged(changes: SimpleChanges) {
    if (changes['configuration']?.currentValue) {
      const newMaxLength = changes['configuration'].currentValue.maxLength;
      const oldMaxLength = changes['configuration'].previousValue?.maxLength;
      return newMaxLength !== oldMaxLength;
    }

    return false;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.isMaxLengthChanged(changes)) {
      if (this.configuration && typeof this.configuration.maxLength === 'number') {
        this.renderer.setAttribute(
          this.element.nativeElement,
          'maxLength',
          this.configuration.maxLength.toString(),
        );
      } else {
        this.renderer.removeAttribute(this.element.nativeElement, 'maxLength');
      }
    }

    if (this.control?.control && this.isRegexpValidationChanged(changes)) {
      this.control.control.removeValidators(this.regExpValidationFn);
      this.regExpValidationFn = createCustomValidation(this.configuration ?? {});
      this.control.control.addValidators(this.regExpValidationFn);
      this.control.control.updateValueAndValidity();
    }

    if (this.control?.control && this.isMaxLengthChanged(changes)) {
      if (this.maxLengthValidatorFn) {
        this.control.control.removeValidators(this.maxLengthValidatorFn);
      }

      this.maxLengthValidatorFn = this.configuration?.maxLength
        ? Validators.maxLength(this.configuration.maxLength)
        : null;
      if (this.maxLengthValidatorFn) {
        this.control.control.addValidators(this.maxLengthValidatorFn);
      }

      this.control.control.updateValueAndValidity();
    }
  }

  @HostListener('input')
  public onInputChange(): void {
    if (!this.configuration?.mask || !this.configuration.maskRegexp) {
      return;
    }

    const input = this.element.nativeElement;
    const previousValue = input.value;

    const unmasked = previousValue.replaceAll(/[^\da-z]/gi, '');
    const transformed = applyTransform(unmasked, this.configuration.transform);
    const masked = applyMask(transformed, this.configuration.mask, this.configuration.maskRegexp);
    const trimmed = masked.replaceAll(/[^\da-z]*$/gi, '');

    this.renderer.setProperty(input, 'value', trimmed);
    this.setControlValue(trimmed);
  }

  private setControlValue(value: string): void {
    if (this.control?.control) {
      this.control.control.setValue(value);
    }
  }
}

function applyTransform(value: string, transform: string | null | undefined): string {
  switch (transform) {
    case 'uppercase':
      return value?.toUpperCase();
    case 'lowercase':
      return value?.toLowerCase();
    case 'none':
      return value;
    case null:
      return value;
    case undefined:
      return value;
    default:
      return value;
  }
}

function applyMask(value: string, mask: string | null, maskRegex: string | null): string {
  if (!mask || !value || !maskRegex) {
    return value;
  }

  const regex = new RegExp(maskRegex);
  return value.replace(regex, mask);
}
